/**
 * BulkPublishDialog Component
 *
 * Dialog for bulk publishing multiple web placements.
 * Three-phase flow:
 * 1. Selection - User selects which placements to publish (all pre-selected)
 * 2. Confirmation - User confirms the action
 * 3. Progress - Shows real-time publishing status per item
 *
 * @module webplacements/components
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { SDialog } from '@modules/ui-components';
import type { WebPlacement, WebPlacementDeployResult } from '@modules/webplacements/types/webplacements.types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Dialog phase state
 */
type DialogPhase = 'selection' | 'confirmation' | 'progress';

/**
 * Per-item publishing status
 */
type PublishStatus = 'pending' | 'publishing' | 'success' | 'error';

/**
 * Placement with publish status (for progress phase)
 */
interface PlacementPublishStatus {
  id: string;
  name: string;
  alias: string;
  status: PublishStatus;
  error?: string;
  /** Typed server code — the analytics privacy gate renders guided copy + CTA. */
  errorCode?: string;
}

const ANALYTICS_PRIVACY_ERROR_CODE = 'analytics_variable_privacy_review_required';
const ANALYTICS_PRIVACY_BULK_COPY =
  'Variables need an analytics privacy review before publishing. Use "Review variables" to decide what analytics may collect, then publish again.';

/**
 * Props for BulkPublishDialog
 */
export interface BulkPublishDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** List of web placements to show for selection */
  webPlacements: WebPlacement[];
  /** Publish function for a single placement */
  onPublish: (webPlacementId: string) => Promise<WebPlacementDeployResult>;
  /** Dialog mode - 'select' allows user selection, 'publish_all' skips to confirmation */
  mode?: 'select' | 'publish_all';
  /**
   * Opens the single-placement deploy flow (which owns the in-dialog analytics
   * privacy review) for an item the bulk publish rejected with the privacy
   * gate. Absent → the guided copy renders without the CTA button.
   */
  onReviewPlacement?: (webPlacementId: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * BulkPublishDialog Component
 *
 * Allows users to select and publish multiple web placements at once.
 */
export const BulkPublishDialog: React.FC<BulkPublishDialogProps> = ({
  open,
  onClose,
  webPlacements,
  onPublish,
  mode = 'select', // Default to current behavior
  onReviewPlacement,
}) => {
  // =========================================================================
  // STATE
  // =========================================================================

  const [phase, setPhase] = useState<DialogPhase>(
    mode === 'publish_all' ? 'confirmation' : 'selection'
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statuses, setStatuses] = useState<PlacementPublishStatus[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [batchSize, setBatchSize] = useState<number>(3); // Batch size for publishing

  // =========================================================================
  // EFFECTS
  // =========================================================================

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      const startPhase = mode === 'publish_all' ? 'confirmation' : 'selection';

      // Pre-select all placements
      setSelectedIds(new Set(webPlacements.map((wp) => wp.id)));
      setSearchQuery('');
      setPhase(startPhase); // Use mode-specific start phase
      setStatuses([]);
      setIsComplete(false);
      setBatchSize(3); // Reset to default
    }
  }, [open, webPlacements, mode]);

  // =========================================================================
  // COMPUTED VALUES
  // =========================================================================

  // Filter placements by search query
  const filteredPlacements = useMemo(() => {
    if (!searchQuery.trim()) return webPlacements;
    const query = searchQuery.toLowerCase();
    return webPlacements.filter(
      (wp) =>
        wp.name.toLowerCase().includes(query) || wp.alias.toLowerCase().includes(query)
    );
  }, [webPlacements, searchQuery]);

  // Count of selected items
  const selectedCount = selectedIds.size;
  const totalCount = webPlacements.length;

  // Check if all visible items are selected
  const allVisibleSelected = filteredPlacements.every((wp) => selectedIds.has(wp.id));

  // Progress stats
  const completedCount = statuses.filter(
    (s) => s.status === 'success' || s.status === 'error'
  ).length;
  const successCount = statuses.filter((s) => s.status === 'success').length;
  const errorCount = statuses.filter((s) => s.status === 'error').length;

  // =========================================================================
  // HANDLERS
  // =========================================================================

  // Toggle single item selection
  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Select/Deselect all visible items
  const handleToggleAll = useCallback(() => {
    if (allVisibleSelected) {
      // Deselect all visible
      const visibleIds = new Set(filteredPlacements.map((wp) => wp.id));
      setSelectedIds((prev) => new Set([...prev].filter((id) => !visibleIds.has(id))));
    } else {
      // Select all visible
      const allVisible = filteredPlacements.map((wp) => wp.id);
      setSelectedIds((prev) => new Set([...prev, ...allVisible]));
    }
  }, [allVisibleSelected, filteredPlacements]);

  // Move to confirmation phase
  const handleProceedToConfirm = useCallback(() => {
    setPhase('confirmation');
  }, []);

  // Go back to selection
  const handleBackToSelection = useCallback(() => {
    setPhase('selection');
  }, []);

  // Update single item status
  const updateStatus = useCallback(
    (id: string, status: PublishStatus, error?: string, errorCode?: string) => {
      setStatuses((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status, error, errorCode } : item))
      );
    },
    []
  );

  // Start publishing all selected items
  const handleStartPublishing = useCallback(async () => {
    setPhase('progress');

    // Get only selected placements
    const selectedPlacements = webPlacements.filter((wp) => selectedIds.has(wp.id));

    // Initialize all as pending
    const initialStatuses: PlacementPublishStatus[] = selectedPlacements.map((wp) => ({
      id: wp.id,
      name: wp.name,
      alias: wp.alias,
      status: 'pending',
    }));
    setStatuses(initialStatuses);

    // ✅ BATCHED SEQUENTIAL PUBLISHING - configurable batch size
    const effectiveBatchSize = Math.max(1, Math.min(10, batchSize)); // Clamp between 1-10

    for (let i = 0; i < selectedPlacements.length; i += effectiveBatchSize) {
      const batch = selectedPlacements.slice(i, i + effectiveBatchSize);

      // Process current batch in parallel
      const batchPromises = batch.map(async (wp) => {
        // Mark as publishing
        updateStatus(wp.id, 'publishing');

        try {
          const result = await onPublish(wp.id);
          if (result.success) {
            updateStatus(wp.id, 'success');
          } else {
            updateStatus(wp.id, 'error', result.error || 'Unknown error', result.errorCode);
          }
        } catch (error) {
          updateStatus(
            wp.id,
            'error',
            error instanceof Error ? error.message : 'Unknown error'
          );
        }
      });

      // Wait for current batch to complete before starting next batch
      await Promise.allSettled(batchPromises);
    }

    setIsComplete(true);
  }, [webPlacements, selectedIds, onPublish, updateStatus, batchSize]);

  // Close dialog (allowed only when not publishing or complete)
  const handleClose = useCallback(() => {
    if (phase === 'progress' && !isComplete) {
      // Don't close while publishing
      return;
    }
    onClose();
  }, [phase, isComplete, onClose]);

  // =========================================================================
  // RENDER HELPERS
  // =========================================================================

  // Status icon for progress phase
  const renderStatusIcon = (status: PublishStatus) => {
    switch (status) {
      case 'pending':
        return <RadioButtonUncheckedIcon color="disabled" />;
      case 'publishing':
        return <CircularProgress size={24} />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
    }
  };

  // =========================================================================
  // RENDER: SELECTION PHASE
  // =========================================================================

  const renderSelectionPhase = () => (
    <>
      {/* Search field */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by name or alias..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 2 }}
      />

      {/* Select all toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <ListItemButton
          dense
          onClick={handleToggleAll}
          sx={{ py: 0.5, px: 1, borderRadius: 1, flexGrow: 0 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Checkbox
              edge="start"
              checked={allVisibleSelected && filteredPlacements.length > 0}
              indeterminate={
                !allVisibleSelected &&
                filteredPlacements.some((wp) => selectedIds.has(wp.id))
              }
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText
            primary={allVisibleSelected ? 'Deselect All' : 'Select All'}
            slotProps={{ primary: { variant: 'body2' } }}
          />
        </ListItemButton>
        <Typography variant="body2" color="text.secondary">
          Selected: {selectedCount} of {totalCount}
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Placements list */}
      <List
        dense
        sx={{
          maxHeight: 300,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {filteredPlacements.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No placements found"
              slotProps={{ primary: { color: 'text.secondary', align: 'center' } }}
            />
          </ListItem>
        ) : (
          filteredPlacements.map((wp) => (
            <ListItemButton
              key={wp.id}
              dense
              onClick={() => handleToggle(wp.id)}
              sx={{ py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={selectedIds.has(wp.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={wp.name}
                secondary={wp.alias}
                slotProps={{
                  primary: { variant: 'body2' },
                  secondary: { variant: 'caption', sx: { fontFamily: 'monospace' } },
                }}
              />
              <Chip
                size="small"
                label={wp.status}
                color={wp.status === 'published' ? 'success' : 'default'}
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </ListItemButton>
          ))
        )}
      </List>
    </>
  );

  // =========================================================================
  // RENDER: CONFIRMATION PHASE
  // =========================================================================

  const renderConfirmationPhase = () => (
    <Box sx={{ py: 2 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Confirm:</strong>
          {mode === 'publish_all'
            ? ` Publish all ${selectedCount} web funnels?`
            : ` Publish ${selectedCount} web funnel${selectedCount !== 1 ? 's' : ''}?`
          }
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          All {mode === 'publish_all' ? 'funnels' : 'selected URLs'} will be updated with the latest content.
        </Typography>
        {selectedCount > 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            Publishing will proceed in batches of {batchSize} to ensure stability.
          </Typography>
        )}
      </Alert>

      {/* Batch Size Configuration */}
      {selectedCount > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Batch Size (1-10):
          </Typography>
          <TextField
            type="number"
            size="small"
            value={batchSize}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value) && value >= 1 && value <= 10) {
                setBatchSize(value);
              }
            }}
            slotProps={{
              htmlInput: { min: 1, max: 10, step: 1 }
            }}
            sx={{ width: 120 }}
            helperText="Placements per batch"
          />
        </Box>
      )}

      <Typography variant="body2" color="text.secondary">
        Selected placements:
      </Typography>
      <Box
        component="ul"
        sx={{ mt: 1, pl: 2, maxHeight: 150, overflow: 'auto', m: 0 }}
      >
        {webPlacements
          .filter((wp) => selectedIds.has(wp.id))
          .map((wp) => (
            <Typography
              component="li"
              variant="body2"
              key={wp.id}
              sx={{ fontFamily: 'monospace' }}
            >
              {wp.alias}
            </Typography>
          ))}
      </Box>
    </Box>
  );

  // =========================================================================
  // RENDER: PROGRESS PHASE
  // =========================================================================

  const renderProgressPhase = () => (
    <>
      {/* Progress list */}
      <List
        dense
        sx={{
          maxHeight: 300,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
        }}
      >
        {statuses.map((item) => {
          const isPrivacyGate = item.status === 'error' && item.errorCode === ANALYTICS_PRIVACY_ERROR_CODE;
          return (
            <ListItem
              key={item.id}
              sx={{ py: 1 }}
              secondaryAction={isPrivacyGate && onReviewPlacement ? (
                <Chip
                  label="Review variables"
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={() => onReviewPlacement(item.id)}
                  data-testid={`bulk-publish-review-variables-${item.id}`}
                />
              ) : undefined}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{renderStatusIcon(item.status)}</ListItemIcon>
              <ListItemText
                primary={item.name}
                secondary={
                  item.status === 'error'
                    // The privacy gate is guided, not raw server text.
                    ? (isPrivacyGate ? ANALYTICS_PRIVACY_BULK_COPY : item.error)
                    : item.status === 'publishing'
                      ? 'Publishing...'
                      : item.status === 'success'
                        ? 'Published'
                        : 'Pending'
                }
                slotProps={{
                  primary: { variant: 'body2' },
                  secondary: {
                    variant: 'caption',
                    color: item.status === 'error' && !isPrivacyGate ? 'error' : 'text.secondary',
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>

      {/* Progress summary */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Progress: {completedCount}/{statuses.length} completed
        </Typography>
        {isComplete && (
          <>
            {successCount > 0 && (
              <Chip size="small" label={`${successCount} success`} color="success" />
            )}
            {errorCount > 0 && (
              <Chip size="small" label={`${errorCount} failed`} color="error" />
            )}
          </>
        )}
      </Box>
    </>
  );

  // =========================================================================
  // DIALOG CONFIG
  // =========================================================================

  // Dialog title based on phase
  const getTitle = () => {
    switch (phase) {
      case 'selection':
        return 'Publish Web Funnels';
      case 'confirmation':
        return 'Confirm Publishing';
      case 'progress':
        return 'Publishing Web Funnels';
    }
  };

  // Primary action config
  const getPrimaryAction = () => {
    switch (phase) {
      case 'selection':
        return {
          label: `Publish Selected (${selectedCount})`,
          onClick: handleProceedToConfirm,
          disabled: selectedCount === 0,
        };
      case 'confirmation':
        return {
          label: 'Yes, Publish',
          onClick: handleStartPublishing,
        };
      case 'progress':
        return {
          label: 'Close',
          onClick: handleClose,
          disabled: !isComplete,
        };
    }
  };

  // Secondary action config
  const getSecondaryAction = () => {
    switch (phase) {
      case 'selection':
        return {
          label: 'Cancel',
          onClick: handleClose,
        };
      case 'confirmation':
        return {
          label: 'Back',
          onClick: handleBackToSelection,
        };
      case 'progress':
        return undefined; // No secondary action during progress
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title={getTitle()}
      maxWidth="sm"
      fullWidth
      // Multi-phase flow (selection / confirmation / progress) — footer
      // buttons are the only dismiss. No header X.
      showCloseButton={false}
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
    >
      {phase === 'selection' && mode === 'select' && renderSelectionPhase()}
      {phase === 'confirmation' && renderConfirmationPhase()}
      {phase === 'progress' && renderProgressPhase()}
    </SDialog>
  );
};

export default BulkPublishDialog;
